/**
 * Agent 管理器
 * 管理和调度 AI Agent 执行任务
 */

import fs from 'fs-extra';
import path from 'path';
import yaml from 'js-yaml';
import { logger } from '../utils/logger';
import { Task } from '../types';

export interface AgentCapability {
  id: string;
  name: string;
  description: string;
  required?: boolean;
}

export interface AgentTool {
  id: string;
  name: string;
  type: string;
  config?: any;
}

export interface AgentPrompts {
  system: string;
  thinking?: string;
  output?: string;
}

export interface Agent {
  id: string;
  name: string;
  description: string;
  avatar: string;
  category: string;
  status: 'active' | 'inactive';
  version: string;
  capabilities: AgentCapability[];
  tools: AgentTool[];
  prompts: AgentPrompts;
  taskTypes?: string[];
  collaboration?: {
    worksWith?: string[];
    provides?: string[];
    requires?: string[];
  };
}

export interface AgentExecutionContext {
  task: Task;
  previousResults?: any[];
  projectContext?: {
    language?: string;
    framework?: string;
    dependencies?: string[];
  };
}

export interface AgentExecutionResult {
  agentId: string;
  taskId: string;
  success: boolean;
  output: string;
  artifacts?: {
    type: string;
    path: string;
    content?: string;
  }[];
  suggestions?: string[];
  nextSteps?: string[];
  executionTime: number;
}

export class AgentManager {
  private agents: Map<string, Agent> = new Map();
  private agentDir: string;
  
  constructor(workflowDir?: string) {
    const projectRoot = workflowDir || process.cwd();
    this.agentDir = path.join(projectRoot, '.workflow', 'agents');
  }
  
  /**
   * 初始化 Agent 管理器
   */
  async initialize(): Promise<void> {
    // 先加载内置 Agent
    await this.loadBuiltinAgents();
    // 再加载自定义 Agent（可以覆盖内置的）
    await this.loadAgents();
  }
  
  /**
   * 加载内置 Agent
   */
  private async loadBuiltinAgents(): Promise<void> {
    try {
      // 动态导入避免循环依赖
      const { getBuiltinAgents } = await import('./builtin-agents');
      const builtinAgents = getBuiltinAgents();
      
      for (const agent of builtinAgents) {
        this.agents.set(agent.id, agent);
        logger.debug(`Loaded builtin agent: ${agent.id} (${agent.name})`);
      }
      
      logger.info(`Loaded ${builtinAgents.length} builtin agents`);
    } catch (error) {
      logger.error('Failed to load builtin agents:', error);
    }
  }
  
  /**
   * 加载所有 Agent 配置
   */
  private async loadAgents(): Promise<void> {
    if (!await fs.pathExists(this.agentDir)) {
      logger.warn('Agent directory not found:', this.agentDir);
      return;
    }
    
    const files = await fs.readdir(this.agentDir);
    const agentFiles = files.filter(f => f.endsWith('.yaml') || f.endsWith('.yml'));
    
    for (const file of agentFiles) {
      try {
        const content = await fs.readFile(path.join(this.agentDir, file), 'utf-8');
        const agent = yaml.load(content) as Agent;
        
        if (agent && agent.id) {
          this.agents.set(agent.id, agent);
          logger.debug(`Loaded agent: ${agent.id} (${agent.name})`);
        }
      } catch (error) {
        logger.error(`Failed to load agent from ${file}:`, error);
      }
    }
    
    logger.info(`Loaded ${this.agents.size} agents`);
  }
  
  /**
   * 获取所有可用的 Agent
   */
  getAgents(): Agent[] {
    return Array.from(this.agents.values()).filter(a => a.status === 'active');
  }
  
  /**
   * 根据 ID 获取 Agent
   */
  getAgent(agentId: string): Agent | undefined {
    return this.agents.get(agentId);
  }
  
  /**
   * 根据任务类型推荐合适的 Agent
   */
  recommendAgent(task: Task): Agent | undefined {
    const taskType = task.metadata?.type || 'feature';
    
    // 查找支持该任务类型的 Agent
    const candidates = this.getAgents().filter(agent => {
      if (!agent.taskTypes) return true; // 没有限制的 Agent 可以处理所有类型
      return agent.taskTypes.includes(taskType);
    });
    
    // 根据任务内容进一步筛选
    if (task.content.toLowerCase().includes('review') || 
        task.content.toLowerCase().includes('审查')) {
      return candidates.find(a => a.id === 'reviewer');
    }
    
    if (task.content.toLowerCase().includes('document') || 
        task.content.toLowerCase().includes('文档') ||
        task.content.toLowerCase().includes('readme')) {
      return candidates.find(a => a.id === 'writer');
    }
    
    // 默认使用开发者 Agent
    return candidates.find(a => a.id === 'developer') || candidates[0];
  }
  
  /**
   * 执行 Agent 任务
   * 注意：这是一个模拟执行，实际执行需要集成 AI 服务
   */
  async executeTask(
    agentId: string, 
    context: AgentExecutionContext
  ): Promise<AgentExecutionResult> {
    const agent = this.getAgent(agentId);
    if (!agent) {
      throw new Error(`Agent not found: ${agentId}`);
    }
    
    const startTime = Date.now();
    
    logger.info(`Agent ${agent.name} executing task: ${context.task.content}`);
    
    // 构建执行提示
    const prompt = this.buildPrompt(agent, context);
    
    // TODO: 这里应该调用实际的 AI 服务
    // 例如：OpenAI API、Claude API 等
    // const result = await this.callAIService(prompt);
    
    // 模拟执行结果
    const mockResult = this.mockExecution(agent, context);
    
    const executionTime = Date.now() - startTime;
    
    return {
      agentId: agent.id,
      taskId: context.task.id,
      success: true,
      output: mockResult.output,
      artifacts: mockResult.artifacts,
      suggestions: mockResult.suggestions,
      nextSteps: mockResult.nextSteps,
      executionTime
    };
  }
  
  /**
   * 构建 Agent 执行提示
   */
  private buildPrompt(agent: Agent, context: AgentExecutionContext): string {
    let prompt = agent.prompts.system + '\n\n';
    
    // 添加任务信息
    prompt += `## 任务\n${context.task.content}\n\n`;
    
    // 添加任务详情
    if (context.task.metadata?.description) {
      prompt += `### 详细描述\n${context.task.metadata.description}\n\n`;
    }
    
    // 添加项目上下文
    if (context.projectContext) {
      prompt += `### 项目上下文\n`;
      if (context.projectContext.language) {
        prompt += `- 编程语言: ${context.projectContext.language}\n`;
      }
      if (context.projectContext.framework) {
        prompt += `- 框架: ${context.projectContext.framework}\n`;
      }
      prompt += '\n';
    }
    
    // 添加思考流程
    if (agent.prompts.thinking) {
      prompt += `### 思考流程\n${agent.prompts.thinking}\n\n`;
    }
    
    // 添加输出要求
    if (agent.prompts.output) {
      prompt += `### 输出要求\n${agent.prompts.output}\n\n`;
    }
    
    return prompt;
  }
  
  /**
   * 模拟 Agent 执行（用于演示）
   */
  private mockExecution(agent: Agent, context: AgentExecutionContext): {
    output: string;
    artifacts?: any[];
    suggestions?: string[];
    nextSteps?: string[];
  } {
    switch (agent.id) {
      case 'developer':
        return {
          output: `已分析任务需求，建议使用以下技术方案：

1. **技术选型**
   - 前端：React + TypeScript + Tailwind CSS
   - 后端：Node.js + Express + PostgreSQL
   - 部署：Docker + GitHub Actions

2. **实现步骤**
   - 设计数据模型
   - 实现 API 接口
   - 开发前端界面
   - 编写测试用例
   - 配置 CI/CD

3. **预计工时**：16小时`,
          artifacts: [
            {
              type: 'code',
              path: 'src/api/index.ts',
              content: '// API implementation'
            }
          ],
          suggestions: [
            '考虑使用 Redis 缓存提升性能',
            '实现输入验证防止注入攻击'
          ],
          nextSteps: [
            '创建项目基础结构',
            '设计数据库 Schema',
            '实现核心 API'
          ]
        };
        
      case 'reviewer':
        return {
          output: `代码审查完成，发现以下问题：

## 必须修复
1. **[安全]** SQL 注入风险
   - 位置：api/users.ts:45
   - 建议：使用参数化查询

2. **[性能]** N+1 查询问题
   - 位置：services/post.ts:78
   - 建议：使用 JOIN 或预加载

## 建议改进
1. 添加输入验证
2. 完善错误处理
3. 增加单元测试

## 代码质量评分：7/10`,
          suggestions: [
            '使用 TypeScript 严格模式',
            '添加 ESLint 规则',
            '配置 pre-commit hooks'
          ]
        };
        
      case 'writer':
        return {
          output: `已生成技术文档大纲：

# API 文档

## 概述
本 API 提供用户管理和认证功能。

## 快速开始
\`\`\`bash
npm install @company/api-client
\`\`\`

## 接口列表
- POST /api/auth/login
- POST /api/auth/register
- GET /api/users/profile

## 详细文档
请参考完整的 API 参考文档。`,
          artifacts: [
            {
              type: 'document',
              path: 'docs/API.md',
              content: '# API Reference\n...'
            }
          ],
          nextSteps: [
            '完善接口参数说明',
            '添加错误码列表',
            '编写集成示例'
          ]
        };
        
      default:
        return {
          output: `Agent ${agent.name} 已处理任务：${context.task.content}`
        };
    }
  }
  
  /**
   * 获取 Agent 协作建议
   */
  getCollaborationSuggestions(currentAgent: Agent, task: Task): Agent[] {
    if (!currentAgent.collaboration?.worksWith) {
      return [];
    }
    
    return currentAgent.collaboration.worksWith
      .map(id => this.getAgent(id))
      .filter((a): a is Agent => a !== undefined);
  }
}